import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, CornerDownLeft, FolderTree, Search, X } from "lucide-react";
import { api, unwrap } from "@/api/client";
import { useFocusTrap, useScrollLock } from "@/components/ui/focus";
import { flattenTree, useCategoryTree } from "@/features/catalog/api";
import { cn } from "@/lib/cn";
import { useDebounced, useLocalList } from "@/lib/hooks";
import { imageUrl } from "@/lib/image";
import { formatNaira } from "@/lib/money";

type Item =
  | { kind: "query"; label: string; to: string }
  | { kind: "recent"; label: string; to: string }
  | { kind: "category"; label: string; hint: string; to: string }
  | { kind: "product"; label: string; hint: string; image?: string; to: string };

/** ⌘K / Ctrl+K command palette: typo-tolerant search with category and listing suggestions. */
export function SearchCommand({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [recent, pushRecent, clearRecent] = useLocalList("rs_recent_searches");
  const debounced = useDebounced(q.trim(), 200);
  const tree = useCategoryTree();
  useFocusTrap(ref, open);
  useScrollLock(open);

  const live = useQuery({
    queryKey: ["search-suggest", debounced],
    queryFn: () => unwrap(api.GET("/api/v1/products/", { params: { query: { q: debounced, page_size: 5, sort: "relevance" } } })),
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  const items = useMemo<Item[]>(() => {
    const term = q.trim();
    const out: Item[] = [];
    if (term) out.push({ kind: "query", label: `Search for “${term}”`, to: `/search?q=${encodeURIComponent(term)}` });
    else recent.forEach((r) => out.push({ kind: "recent", label: r, to: `/search?q=${encodeURIComponent(r)}` }));
    const lower = term.toLowerCase();
    flattenTree(tree.data)
      .filter((c) => term.length >= 2 && c.name.toLowerCase().includes(lower))
      .slice(0, 4)
      .forEach((c) =>
        out.push({ kind: "category", label: c.name, hint: c.path.slice(0, -1).join(" › ") || "Category", to: `/c/${c.slug}` }),
      );
    (live.data?.results ?? []).forEach((p) =>
      out.push({ kind: "product", label: p.title, hint: formatNaira(p.price), image: p.cover?.url, to: `/p/${p.slug}` }),
    );
    return out;
  }, [q, recent, tree.data, live.data]);

  useEffect(() => setActive(0), [q]);
  useEffect(() => {
    if (open) {
      setQ("");
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const choose = (item: Item | undefined) => {
    if (!item) return;
    if (item.kind === "query" || item.kind === "recent") pushRecent(item.kind === "query" ? q.trim() : item.label);
    onClose();
    navigate(item.to);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[75] flex items-start justify-center p-4 pt-[10vh]">
      <div className="absolute inset-0 animate-fade-in bg-scrim/55 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Search RealityStore"
        className="relative w-full max-w-xl animate-pop-in overflow-hidden rounded-card border border-[var(--border-glow)] bg-elevated shadow-glow-3"
      >
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="size-5 shrink-0 text-cyan" aria-hidden />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((i) => Math.min(i + 1, items.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                choose(items[active]);
              } else if (e.key === "Escape") onClose();
            }}
            role="combobox"
            aria-expanded
            aria-controls="search-results"
            aria-activedescendant={items[active] ? `sr-${active}` : undefined}
            placeholder="Search phones, cars, fashion, stores…"
            className="h-14 flex-1 bg-transparent text-base text-ink placeholder:text-ink-muted focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="grid size-9 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
            aria-label="Close search"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <ul id="search-results" role="listbox" className="max-h-[60vh] overflow-y-auto p-2">
          {!q && recent.length > 0 && (
            <li
              className="flex items-center justify-between px-3 pb-1 pt-2 text-[11px] uppercase tracking-wider text-ink-muted"
              role="presentation"
            >
              Recent
              <button
                type="button"
                className="normal-case tracking-normal text-[var(--primary-text)] hover:underline"
                onClick={clearRecent}
              >
                Clear
              </button>
            </li>
          )}
          {items.map((item, i) => (
            // Keyboard selection is handled by the combobox input (aria-activedescendant).
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={`${item.kind}-${item.to}`}
              id={`sr-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(item)}
              className={cn("flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5", i === active ? "bg-primary/20" : "")}
            >
              <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-fg/[0.05] text-ink-muted">
                {item.kind === "product" && item.image ? (
                  <img src={imageUrl(item.image, 80)} alt="" className="size-full object-cover" />
                ) : item.kind === "category" ? (
                  <FolderTree className="size-4" aria-hidden />
                ) : item.kind === "recent" ? (
                  <Clock className="size-4" aria-hidden />
                ) : (
                  <Search className="size-4" aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-ink">{item.label}</span>
                {"hint" in item && <span className="tabular block truncate text-xs text-ink-muted">{item.hint}</span>}
              </span>
              {i === active && <CornerDownLeft className="size-4 text-ink-muted" aria-hidden />}
            </li>
          ))}
          {q.trim().length >= 2 && !live.isFetching && items.length <= 1 && (
            <li className="px-3 py-6 text-center text-sm text-ink-muted">No quick matches. Press Enter to search everything.</li>
          )}
          {!q && recent.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-ink-muted">Try “iPhone 13 Ikeja”, “Toyota Camry” or “Ankara”.</li>
          )}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
